import {tricontour} from 'd3-tricontour'
import * as d3 from 'd3'

const FONT_SIZE = 12

const LEGEND_BOTTOM = 10
const LEGEND_ITEM_HEIGHT = 30
const LEGEND_ITEM_COLOR_WIDTH = 20
const LEGEND_ITEM_COLOR_LABEL_OFFSET_X = 5
const LEGEND_PADDING_RIGHT = 10

const DEFAULT_COLOR_LIST = ['#26BC68', '#FDDE40', '#FD9C3E', '#DF2349', '#8D2EC0', '#84102D']
const DEFAULT_VALUE_INTERVAL = DEFAULT_COLOR_LIST.length

const CHART_PADDING = 25
const POLAR_WIDTH_OFFSET = 15
const AXIS_LABEL_SPACE = 5
const AXIS_INTERVAL = 5
const AXIS_LABEL_OFFSET = 15

const ANGLE_INTERVAL = 6
const DEG_STEP = 180 / ANGLE_INTERVAL

const PI = Math.PI
const BASE_ANGLE = PI / 180
const SIN = Math.sin
const COS = Math.cos

const tmpCanvas = document.createElement('canvas')
const tmpCtx = tmpCanvas.getContext('2d')

const getColorHex = () => Math.round(Math.random() * 255).toString(16).padStart(2, '0')

const getThresholdOpts = (minZ, maxZ, interval) => {
  let thresholdOpts = new Array(interval)
  let zDiff = maxZ - minZ
  let valueStep = zDiff / (interval + 1)

  for (let i = 0; i < interval; i++) {
    thresholdOpts[i] = [
      maxZ < interval ? Number((minZ + i * valueStep).toFixed(3)) : Math.round(minZ + i * valueStep),
      DEFAULT_COLOR_LIST[i] || `#${getColorHex()}${getColorHex()}${getColorHex()}`
    ]
  }

  return thresholdOpts
}

const convertToXYZ = (vertices, cx, cy, scale) => vertices.map(([r, theta, z]) => {
  let angle = theta * BASE_ANGLE
  r *= scale
  return [cx + r * SIN(angle), cy - r * COS(angle), z]
})

const clearCanvas = canvas => {
  let ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  return ctx
}

const drawPolarCoordinate = (tmpCtx, cx, cy, r, maxVal, decimal) => {
  let rStep = r / AXIS_INTERVAL
  tmpCtx.strokeStyle = '#000'
  for (let i = 0; i < AXIS_INTERVAL; i++) {
    tmpCtx.beginPath()

    if (i > 0) {
      tmpCtx.strokeStyle = '#ccc'
    }

    tmpCtx.arc(cx, cy, r - rStep * i, 0, PI * 2, false)
    tmpCtx.stroke()
    tmpCtx.closePath()
  }

  tmpCtx.font = `${FONT_SIZE}px microsoft yahei`
  tmpCtx.fillStyle = '#000'
  tmpCtx.textBaseline = 'middle'
  for (let i = 0; i < ANGLE_INTERVAL; i++) {
    let deg = i * DEG_STEP
    let angle = deg * BASE_ANGLE
    let sin = SIN(angle)
    let cos = COS(angle)
    let offsetX = r * sin
    let offsetY = r * cos
    let beginX = cx + offsetX
    let beginY = cy - offsetY
    let endX = cx - offsetX
    let endY = cy + offsetY
    let axisOffsetX = AXIS_LABEL_SPACE * sin
    let axisOffsetY = AXIS_LABEL_SPACE * cos

    tmpCtx.beginPath()
    tmpCtx.strokeStyle = '#ccc'
    tmpCtx.moveTo(beginX, beginY)
    tmpCtx.lineTo(endX, endY)
    tmpCtx.stroke()
    tmpCtx.closePath()
    tmpCtx.beginPath()
    tmpCtx.strokeStyle = '#000'
    tmpCtx.moveTo(beginX, beginY)
    tmpCtx.lineTo(beginX - axisOffsetX, beginY + axisOffsetY)
    tmpCtx.stroke()
    tmpCtx.closePath()
    tmpCtx.beginPath()
    tmpCtx.moveTo(endX, endY)
    tmpCtx.lineTo(endX + axisOffsetX, endY - axisOffsetY)
    tmpCtx.stroke()
    tmpCtx.closePath()

    let axisLabelOffsetX = AXIS_LABEL_OFFSET * sin
    let axisLabelOffsetY = AXIS_LABEL_OFFSET * cos

    if (deg === 0) {
      tmpCtx.textAlign = 'center'
      tmpCtx.beginPath()
      tmpCtx.strokeStyle = '#000'
      tmpCtx.moveTo(beginX, beginY)
      tmpCtx.lineTo(cx, cy)
      tmpCtx.stroke()
      tmpCtx.closePath()
    }

    tmpCtx.textAlign = 'center'
    tmpCtx.fillText(deg.toString(), beginX + axisLabelOffsetX, beginY - axisLabelOffsetY)
    tmpCtx.fillText((deg + 180).toString(), endX - axisLabelOffsetX, endY + axisLabelOffsetY)
  }

  let valStep = maxVal / AXIS_INTERVAL
  tmpCtx.textAlign = 'left'

  for (let i = 0; i <= AXIS_INTERVAL; i++) {
    let y = cy - (r - i * rStep)
    let toX = cx + 3
    let axis = maxVal - i * valStep
    tmpCtx.moveTo(cx, y)
    tmpCtx.lineTo(toX, y)
    tmpCtx.fillText(isNaN(axis) ? '' : axis.toFixed(decimal), toX + 2, y)
  }

  tmpCtx.stroke()
}

const drawLegend = (tmpCtx, x, thresholdOpts, unit) => {
  tmpCtx.textBaseline = 'middle'
  let beginY = tmpCanvas.height - LEGEND_BOTTOM - LEGEND_ITEM_HEIGHT * thresholdOpts.length

  thresholdOpts.forEach((colorValue, index) => {
    let y = beginY + index * LEGEND_ITEM_HEIGHT
    tmpCtx.fillStyle = colorValue[1]
    tmpCtx.fillRect(x, y, LEGEND_ITEM_COLOR_WIDTH, LEGEND_ITEM_HEIGHT)
    tmpCtx.fillStyle = '#000'
    tmpCtx.fillText(`${colorValue[0] || 0}${unit}`, x + LEGEND_ITEM_COLOR_WIDTH + LEGEND_ITEM_COLOR_LABEL_OFFSET_X, y)
  })
}

export default {
  getContourOpts: (dataList, interval = DEFAULT_VALUE_INTERVAL) => {
    let zs = dataList.map(data => data[2])
    let minZ = Math.min(...zs)
    let maxZ = Math.max(...zs)
    let thresholdOpts = getThresholdOpts(minZ, maxZ, interval)

    return {
      interval,
      thresholdOpts,
      minZ,
      maxZ
    }
  },
  getThresholdOpts,
  setMaxVal: (dataList, opts) => {
    let maxVal = Math.max(...dataList.map(data => data[0]))
    let decimal = 0

    if (maxVal % 1 > 0) {
      decimal = 1
      maxVal = parseFloat(maxVal.toFixed(1))
    }

    Object.assign(opts, {
      decimal,
      maxVal
    })
  },
  setChartSize: (canvas, contourOpts, opts, unit) => {
    if (contourOpts === null) {
      return
    }

    let parent = canvas.parentNode
    let width = parent.offsetWidth
    let height = parent.offsetHeight

    tmpCtx.font = `${FONT_SIZE}px microsoft yahei`

    let legendLabelWidths = contourOpts.thresholdOpts.map(([value]) => tmpCtx.measureText(`${value}${unit}`).width)
    let maxLegendLabelWidth = Math.ceil(Math.max(...legendLabelWidths))
    let realLegendWidth = Math.round(LEGEND_ITEM_COLOR_WIDTH + LEGEND_ITEM_COLOR_LABEL_OFFSET_X + maxLegendLabelWidth + LEGEND_PADDING_RIGHT)
    let chartWidth = width - realLegendWidth
    let size = Math.min(chartWidth, height)
    let half = size >> 1
    let r = half - CHART_PADDING
    let polarWidth = size + POLAR_WIDTH_OFFSET

    canvas.width = Math.round(polarWidth + realLegendWidth)
    canvas.height = size

    Object.assign(opts, {
      legendWidth: realLegendWidth,
      cx: polarWidth >> 1,
      cy: height >> 1,
      r,
      scale: r / opts.maxVal
    })
  },
  clearCanvas,
  draw: (canvas, coordinateType, vertices, opts, unit) => {
    let {cx, cy} = opts

    let {width, height} = canvas

    if (coordinateType === 0) {
      vertices = convertToXYZ(vertices, cx, cy, opts.scale)
    }

    const path = d3.geoPath(null, tmpCtx)
    let contours = tricontour()
    let thresholdOpts = opts.thresholdOpts
    tmpCanvas.width = width
    tmpCanvas.height = height
    tmpCtx.clearRect(0, 0, width, height)
    tmpCtx.lineWidth = 0

    opts.thresholdOpts.forEach(([threshold, color]) => {
      tmpCtx.beginPath()
      path(contours.contour(vertices, threshold))
      tmpCtx.fillStyle = color
      tmpCtx.fill()
    })

    drawPolarCoordinate(tmpCtx, cx, cy, opts.r, opts.maxVal, opts.decimal)
    drawLegend(tmpCtx, width - opts.legendWidth, thresholdOpts, unit)

    let ctx = clearCanvas(canvas)
    ctx.fillStyle = '#FFF'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(
      tmpCanvas, // 规定要使用的图像、画布或视频。
      0, 0, // 开始剪切的 x 坐标位置。
      width, height, // 被剪切图像的高度。
      0, 0, // 在画布上放置图像的 x 、y坐标位置。
      width, height // 要使用的图像的宽度、高度
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/PolarContour/util.js